import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Image, Video, AlertCircle, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { newsAPI, categoriesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';

// Static categories
const STATIC_CATEGORIES = [
  { id: 'ekonomi', name: 'Ekonomi', slug: 'ekonomi' },
  { id: 'politika', name: 'Politika', slug: 'politika' },
  { id: 'vizyon-sektor', name: 'Vizyon Sektör', slug: 'vizyon-sektor' },
  { id: 'dunya', name: 'Dünya', slug: 'dunya' },
  { id: 'teknoloji', name: 'Teknoloji', slug: 'teknoloji' },
  { id: 'finans', name: 'Finans', slug: 'finans' },
];

const AdminNewsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    title: '',
    body: '',
    image_url: '',
    youtube_url: '',
    category: '',
    is_featured: false,
  });
  const [categories] = useState(STATIC_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditing);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/giris');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isEditing && id) {
      fetchNews();
    }
  }, [id, isEditing]);

  const fetchNews = async () => {
    try {
      const response = await newsAPI.getById(id);
      if (response.data) {
        setFormData({
          title: response.data.title || '',
          body: response.data.body || '',
          image_url: response.data.image_url || '',
          youtube_url: response.data.youtube_url || '',
          category: response.data.category || '',
          is_featured: response.data.is_featured || false,
        });
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Haber yüklenirken hata oluştu');
      navigate('/admin');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'image_url') {
      setImageError(false);
    }
  }, []);

  const handleCategoryChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, category: e.target.value }));
  }, []);

  const handleFeaturedChange = useCallback((checked) => {
    setFormData((prev) => ({ ...prev, is_featured: checked }));
  }, []);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    // Validation
    if (!formData.title.trim()) {
      setError('Başlık zorunludur');
      return;
    }
    if (!formData.body.trim()) {
      setError('İçerik zorunludur');
      return;
    }
    if (!formData.image_url.trim()) {
      setError('Görsel URL zorunludur');
      return;
    }
    if (!formData.category) {
      setError('Kategori seçimi zorunludur');
      return;
    }

    setLoading(true);

    try {
      if (isEditing) {
        await newsAPI.update(id, formData);
        toast.success('Haber güncellendi');
      } else {
        await newsAPI.create(formData);
        toast.success('Haber oluşturuldu');
      }
      navigate('/admin');
    } catch (error) {
      console.error('Error saving news:', error);
      setError(error.response?.data?.detail || 'Haber kaydedilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || fetchLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-gray-500">Yükleniyor...</div>
      </div>
    );
  }

  const isVizyonSektor = formData.category === 'vizyon-sektor';

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-news-form">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon" type="button">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-lg font-bold text-gray-900">
                {isEditing ? 'Haberi Düzenle' : 'Yeni Haber Ekle'}
              </h1>
              <p className="text-gray-500 text-sm">
                {isEditing ? 'Mevcut haberi güncelleyin' : 'Yeni bir haber oluşturun'}
              </p>
            </div>
          </div>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-[#0284C7] hover:bg-[#0369A1]"
            data-testid="save-news-btn"
            type="button"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Haber İçeriği</CardTitle>
                <CardDescription>Haber başlığı ve içeriğini girin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Başlık *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Haber başlığını girin"
                    className="text-lg"
                    data-testid="news-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="body">İçerik *</Label>
                  <Textarea
                    id="body"
                    name="body"
                    value={formData.body}
                    onChange={handleChange}
                    placeholder="Haber içeriğini yazın..."
                    rows={12}
                    className="resize-none"
                    data-testid="news-body-input"
                  />
                  <p className="text-xs text-gray-500">
                    Her paragraf için yeni satır kullanın
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Media */}
            <Card>
              <CardHeader>
                <CardTitle>Medya</CardTitle>
                <CardDescription>Haber görseli ve video bağlantısı</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="image_url" className="flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Görsel URL *
                  </Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    data-testid="news-image-input"
                  />
                  {formData.image_url && !imageError && (
                    <div className="mt-2 rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                        onError={() => setImageError(true)}
                      />
                    </div>
                  )}
                  {imageError && (
                    <p className="text-sm text-red-500 mt-1">Görsel yüklenemedi. URL yi kontrol edin.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="youtube_url" className="flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    YouTube Video URL
                    {isVizyonSektor && <span className="text-amber-600 text-xs">(Vizyon Sektör için önerilir)</span>}
                  </Label>
                  <Input
                    id="youtube_url"
                    name="youtube_url"
                    value={formData.youtube_url}
                    onChange={handleChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="news-youtube-input"
                  />
                  <p className="text-xs text-gray-500">
                    YouTube video bağlantısını girin (opsiyonel)
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Ayarlar</CardTitle>
                <CardDescription>Kategori ve görünürlük ayarları</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategori *</Label>
                  <div className="relative">
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleCategoryChange}
                      className="w-full h-10 px-3 pr-10 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer"
                      data-testid="news-category-select"
                    >
                      <option value="">Kategori seçin</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.slug}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="flex items-center justify-between py-4 border-t border-gray-100">
                  <div>
                    <Label htmlFor="is_featured" className="cursor-pointer">Öne Çıkan Haber</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Ana sayfada büyük gösterilir
                    </p>
                  </div>
                  <Switch
                    id="is_featured"
                    checked={formData.is_featured}
                    onCheckedChange={handleFeaturedChange}
                    data-testid="news-featured-switch"
                  />
                </div>
              </CardContent>
            </Card>

            {isVizyonSektor && (
              <Alert className="bg-purple-50 border-purple-200">
                <Video className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-purple-700 text-sm">
                  Vizyon Sektör kategorisindeki haberler YouTube video bağlantısı ile 
                  video galerisinde gösterilir.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminNewsForm;
